/**
 * Scenario OS — Validation Layer
 *
 * Public API for runtime validation of Scenario Contracts.
 *
 * Usage:
 *   import { parseScenarioContract, validateContract } from '@cypremos/shared'
 *
 *   const result = parseScenarioContract(untrustedJson)
 *   if (!result.success) {
 *     console.error(result.error.issues)
 *     return
 *   }
 *
 *   const report = validateContract(result.data, capabilityManifest)
 *   if (!report.valid) {
 *     console.error(report.issues)
 *   }
 */
import type { ScenarioContract, CapabilityManifest } from '../types/scenario-contract.js'
import {
  ScenarioContractSchema,
  CapabilityManifestSchema,
  ScenarioResultSchema,
} from './scenario-contract.schema.js'
import {
  validateContract,
  validateCapability,
  validateConsistency,
} from './capability-validator.js'
import type {
  ScenarioValidationReport,
  ScenarioValidationIssue,
  ScenarioValidationSeverity,
  FullValidationOptions,
  CapabilityValidationOptions,
} from './capability-validator.js'

// Re-export types
export type {
  ScenarioValidationReport,
  ScenarioValidationIssue,
  ScenarioValidationSeverity,
  FullValidationOptions,
  CapabilityValidationOptions,
}

// Re-export validators
export { validateContract, validateCapability, validateConsistency }

// Re-export all Zod schemas
export * from './scenario-contract.schema.js'

// ════════════════════════════════════════════════════════════
// Convenience parse functions
// ════════════════════════════════════════════════════════════

export function parseScenarioContract(data: unknown) {
  return ScenarioContractSchema.safeParse(data)
}

export function parseCapabilityManifest(data: unknown) {
  return CapabilityManifestSchema.safeParse(data)
}

export function parseScenarioResult(data: unknown) {
  return ScenarioResultSchema.safeParse(data)
}

/**
 * Full validation pipeline: structural (Zod) + consistency + capability.
 *
 * Use this at every system boundary:
 *   - AI generator output
 *   - Interactive recorder export
 *   - External file import
 *   - API endpoint ingestion
 */
export function validateScenarioFull(
  data: unknown,
  capabilityManifest?: CapabilityManifest,
  options?: FullValidationOptions,
): { structuralErrors: unknown[] } | { contract: ScenarioContract; report: ScenarioValidationReport } {
  const parsed = ScenarioContractSchema.safeParse(data)
  if (!parsed.success) {
    return {
      structuralErrors: parsed.error.issues.map(i => ({
        path: i.path.join('.'),
        message: i.message,
        code: i.code,
      })),
    }
  }

  const report = validateContract(
    parsed.data,
    capabilityManifest?.capability,
    options,
  )

  return { contract: parsed.data, report }
}
